import { useEffect, useMemo, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DataGrid from "@/components/DataGrid";
import ColumnManager from "@/components/ColumnManager";
import CustomerForm from "@/components/CustomerForm";
import ImportModal from "@/components/ImportModal";
import BulkEditModal from "@/components/BulkEditModal";
import NotesModal from "@/components/NotesModal";
import TopReportBar from "@/components/TopReportBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
const STAGE_KEYS = ["asama1", "asama2", "asama3", "asama4"];
import { Plus, Upload, Download, Settings2, Search, Trash2, Loader2, Archive, ArchiveRestore, Pencil, Filter, PieChart, X } from "lucide-react";

export default function RecordsPage() {
  const { hasPerm, isAdmin } = useAuth();
  const [columns, setColumns] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [colMgr, setColMgr] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [view, setView] = useState("active");
  const [notesRec, setNotesRec] = useState(null);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [asamasizOnly, setAsamasizOnly] = useState(false);
  const [reports, setReports] = useState([]);
  const [hiddenGroups, setHiddenGroups] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const canEdit = hasPerm("can_edit");
  const canDelete = hasPerm("can_delete");
  const canImport = hasPerm("can_import");
  const canExport = hasPerm("can_export");
  const canManageCols = hasPerm("can_manage_columns");
  const canManageNotes = hasPerm("can_manage_notes");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/columns"),
        api.get("/records", { params: { archived: view === "archived" } }),
      ]);
      setColumns(c.data);
      setRecords(r.data);
      setSelected([]);
      api.get("/reports").then((res) => setReports(res.data)).catch(() => {});
      api.get("/topbar-config").then((res) => setHiddenGroups(res.data.hidden || [])).catch(() => {});
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    load();
  }, [load]);

  const persistColumns = async (next) => {
    setColumns(next);
    try {
      await api.put("/columns", { columns: next });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const onReorder = (fromKey, toKey) => {
    const arr = [...columns];
    const fromIdx = arr.findIndex((c) => c.key === fromKey);
    const toIdx = arr.findIndex((c) => c.key === toKey);
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    persistColumns(arr);
  };

  const onResize = (key, width, persist) => {
    setColumns((cols) => {
      const next = cols.map((c) => (c.key === key ? { ...c, width: width ?? c.width } : c));
      if (persist) api.put("/columns", { columns: next }).catch(() => {});
      return next;
    });
  };

  const onCellChange = async (rec, key, value) => {
    const newValues = { ...rec.values, [key]: value };
    if (key === "il") newValues.ilce = "";
    setRecords((rs) => rs.map((r) => (r.id === rec.id ? { ...r, values: newValues } : r)));
    try {
      const res = await api.put(`/records/${rec.id}`, { values: newValues });
      if (res?.data?.values) {
        setRecords((rs) => rs.map((r) => (r.id === rec.id ? { ...r, values: res.data.values } : r)));
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
      load();
    }
  };

  const addRow = async (rec) => {
    setRecords((rs) => [...rs, rec]);
  };

  const onHideColumn = (key) =>
    persistColumns(columns.map((c) => (c.key === key ? { ...c, visible: false } : c)));

  const onDeleteColumn = (col) => {
    if (!window.confirm(`"${col.label}" sütunu silinsin mi?`)) return;
    persistColumns(columns.filter((c) => c.key !== col.key));
    toast.success("Sütun silindi");
  };

  const onNotesCountChange = (recordId, count) =>
    setRecords((rs) => rs.map((r) => (r.id === recordId ? { ...r, note_count: count } : r)));

  const onFilterChange = (key, val) => setFilters((f) => ({ ...f, [key]: val }));
  const isActiveFilter = (v) => {
    if (v && typeof v === "object") return v.val != null && String(v.val).trim() !== "";
    return v && String(v).trim();
  };
  const activeFilterCount = Object.values(filters).filter(isActiveFilter).length;

  const onReportFilter = (key, val) => {
    setAsamasizOnly(false);
    setFilters((f) => ({ ...f, [key]: val }));
    setShowFilters(true);
    toast.success(`Filtre uygulandı: ${val}`);
  };

  const onMetric = ({ mode, key, value }) => {
    if (mode === "all") {
      setFilters({});
      setAsamasizOnly(false);
    } else if (mode === "asamasiz") {
      setFilters({});
      setAsamasizOnly(true);
    } else {
      setAsamasizOnly(false);
      setFilters({ [key]: value });
    }
  };

  const persistReports = async (next) => {
    setReports(next);
    try {
      await api.put("/reports", { reports: next });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };
  const onAddReport = (rep) => persistReports([...reports, { ...rep, id: crypto.randomUUID() }]);
  const onRemoveReport = (id) => persistReports(reports.filter((r) => r.id !== id));
  const onReorderReports = (next) => persistReports(next);

  const onToggleGroups = async (next) => {
    setHiddenGroups(next);
    try {
      await api.put("/topbar-config", { hidden: next });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const onFillColumn = async (key, startId, lines) => {
    const idx = paged.findIndex((r) => r.id === startId);
    if (idx < 0) return;
    const next = [...records];
    const updates = [];
    lines.forEach((line, i) => {
      const target = paged[idx + i];
      if (!target) return;
      const ri = next.findIndex((r) => r.id === target.id);
      if (ri < 0) return;
      const nv = { ...next[ri].values, [key]: line };
      next[ri] = { ...next[ri], values: nv };
      updates.push({ id: target.id, values: nv });
    });
    setRecords(next);
    const results = await Promise.all(
      updates.map((u) =>
        api.put(`/records/${u.id}`, { values: u.values })
          .then((r) => ({ id: u.id, values: r.data?.values }))
          .catch(() => null)
      )
    );
    setRecords((cur) =>
      cur.map((r) => {
        const m = results.find((x) => x && x.id === r.id);
        return m && m.values ? { ...r, values: m.values } : r;
      })
    );
    toast.success(`${updates.length} hücre yapıştırıldı`);
  };

  const deleteRow = async (rec) => {
    if (!window.confirm("Bu kayıt kalıcı olarak silinsin mi? Bu işlem geri alınamaz.")) return;
    try {
      await api.delete(`/records/${rec.id}`);
      setRecords((rs) => rs.filter((r) => r.id !== rec.id));
      setSelected((s) => s.filter((x) => x !== rec.id));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const deleteSelected = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`${selected.length} kayıt kalıcı olarak silinsin mi?`)) return;
    try {
      await api.post("/records/bulk-delete", { ids: selected });
      setRecords((rs) => rs.filter((r) => !selected.includes(r.id)));
      setSelected([]);
      toast.success("Seçili kayıtlar silindi");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const bulkArchive = async (archived) => {
    if (selected.length === 0) return;
    try {
      await api.post("/records/bulk-archive", { ids: selected, archived });
      setRecords((rs) => rs.filter((r) => !selected.includes(r.id)));
      setSelected([]);
      toast.success(archived ? "Kayıtlar arşivlendi" : "Kayıtlar arşivden çıkarıldı");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const archiveRow = async (rec) => {
    const archived = view === "active";
    try {
      await api.post("/records/bulk-archive", { ids: [rec.id], archived });
      setRecords((rs) => rs.filter((r) => r.id !== rec.id));
      setSelected((s) => s.filter((x) => x !== rec.id));
      toast.success(archived ? "Arşivlendi" : "Arşivden çıkarıldı");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const bulkUpdate = async (field, value) => {
    try {
      await api.post("/records/bulk-update", { ids: selected, field, value });
      setRecords((rs) =>
        rs.map((r) =>
          selected.includes(r.id) ? { ...r, values: { ...r.values, [field]: value } } : r
        )
      );
      toast.success(`${selected.length} kayıt güncellendi`);
      setSelected([]);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const doExport = async () => {
    try {
      const res = await api.get("/records/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "mwcrmpro_export.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const filtered = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => isActiveFilter(v));
    const typeMap = Object.fromEntries(columns.map((c) => [c.key, c.type]));
    const s = search.trim().toLowerCase();
    return records.filter((r) => {
      if (s && !Object.values(r.values || {}).some((v) => String(v).toLowerCase().includes(s)))
        return false;
      for (const [k, v] of active) {
        if (v && typeof v === "object") {
          const num = parseFloat(r.values?.[k]);
          if (isNaN(num)) return false;
          const a = parseFloat(v.val);
          const b = parseFloat(v.val2);
          switch (v.op) {
            case "<=": if (!(num <= a)) return false; break;
            case "=": if (num !== a) return false; break;
            case ">": if (!(num > a)) return false; break;
            case "<": if (!(num < a)) return false; break;
            case "between":
              if (!(num >= a && (isNaN(b) || num <= b))) return false;
              break;
            default: if (!(num >= a)) return false; // ">="
          }
          continue;
        }
        const cell = String(r.values?.[k] ?? "").toLowerCase();
        const fv = String(v).toLowerCase();
        if (typeMap[k] === "select") {
          if (cell !== fv) return false;
        } else if (!cell.includes(fv)) {
          return false;
        }
      }
      if (asamasizOnly && !STAGE_KEYS.every((k) => !(r.values?.[k] ?? "").toString().trim()))
        return false;
      return true;
    });
  }, [records, search, filters, columns, asamasizOnly]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    setPage(1);
  }, [search, view, filters, asamasizOnly]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page]
  );

  return (
    <div className="flex flex-col h-full p-4 md:p-6 gap-4 overflow-hidden">
      {!loading && (
        <TopReportBar
          records={records}
          columns={columns}
          filters={filters}
          asamasizOnly={asamasizOnly}
          onMetric={onMetric}
          reports={reports}
          onAddReport={onAddReport}
          onRemoveReport={onRemoveReport}
          onReorderReports={onReorderReports}
          hiddenGroups={hiddenGroups}
          onToggleGroups={onToggleGroups}
          isAdmin={isAdmin}
        />
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Kayıtlar</h1>
          <p className="text-sm text-muted-foreground">
            {filtered.length} kayıt · {columns.filter((c) => c.visible).length}/{columns.length} sütun görünür
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-border p-0.5 bg-secondary/50">
            <button
              onClick={() => setView("active")}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                view === "active" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="view-active"
            >
              Aktif
            </button>
            <button
              onClick={() => setView("archived")}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors flex items-center gap-1.5 ${
                view === "archived" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="view-archived"
            >
              <Archive className="w-3.5 h-3.5" /> Arşiv
            </button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ara..."
              className="pl-8 w-44"
              data-testid="records-search"
            />
          </div>
          {canManageCols && (
            <Button variant="outline" onClick={() => setColMgr(true)} data-testid="open-column-manager">
              <Settings2 className="w-4 h-4 mr-1.5" /> Sütunlar
            </Button>
          )}
          <Button
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters((v) => !v)}
            data-testid="toggle-filters"
          >
            <Filter className="w-4 h-4 mr-1.5" /> Filtre
            {activeFilterCount > 0 && (
              <span className="ml-1.5 bg-primary-foreground/20 rounded px-1 text-xs">{activeFilterCount}</span>
            )}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="icon" onClick={() => setFilters({})} title="Filtreleri temizle" data-testid="clear-filters">
              <X className="w-4 h-4" />
            </Button>
          )}
          {canImport && view === "active" && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import">
              <Upload className="w-4 h-4 mr-1.5" /> Excel Yükle
            </Button>
          )}
          {canExport && (
            <Button variant="outline" onClick={doExport} data-testid="export-button">
              <Download className="w-4 h-4 mr-1.5" /> İndir
            </Button>
          )}
          {canEdit && view === "active" && (
            <Button onClick={() => setFormOpen(true)} data-testid="add-record-button">
              <Plus className="w-4 h-4 mr-1.5" /> Yeni Kayıt
            </Button>
          )}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-secondary rounded-lg px-4 py-2 text-sm" data-testid="bulk-action-bar">
          <span className="font-medium">{selected.length} kayıt seçili</span>
          {canEdit && view === "active" && (
            <Button variant="outline" size="sm" onClick={() => setBulkEditOpen(true)} data-testid="bulk-edit-button">
              <Pencil className="w-4 h-4 mr-1.5" /> Toplu Düzenle
            </Button>
          )}
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => bulkArchive(view === "active")}
              data-testid="bulk-archive-button"
            >
              {view === "active" ? (
                <><Archive className="w-4 h-4 mr-1.5" /> Arşivle</>
              ) : (
                <><ArchiveRestore className="w-4 h-4 mr-1.5" /> Arşivden Çıkar</>
              )}
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" size="sm" onClick={deleteSelected} data-testid="delete-selected-button">
              <Trash2 className="w-4 h-4 mr-1.5" /> Sil
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <DataGrid
          columns={columns}
          records={paged}
          onReorder={onReorder}
          onResize={onResize}
          onCellChange={onCellChange}
          onDeleteRow={deleteRow}
          onArchiveRow={archiveRow}
          onOpenNotes={setNotesRec}
          onFillColumn={onFillColumn}
          view={view}
          onHideColumn={onHideColumn}
          onDeleteColumn={onDeleteColumn}
          canManageCols={canManageCols}
          showFilters={showFilters}
          filters={filters}
          onFilterChange={onFilterChange}
          canEdit={canEdit}
          canDelete={canDelete}
          selected={selected}
          setSelected={setSelected}
        />
      )}

      {!loading && filtered.length > pageSize && (
        <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="pagination-bar">
          <span>
            {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filtered.length)} / {filtered.length} kayıt
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="page-prev"
            >
              Önceki
            </Button>
            <span className="font-medium text-foreground">
              Sayfa {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="page-next"
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}

      <BulkEditModal
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        columns={columns}
        count={selected.length}
        onApply={bulkUpdate}
      />

      <NotesModal
        open={!!notesRec}
        onOpenChange={(o) => !o && setNotesRec(null)}
        record={notesRec}
        canManageNotes={canManageNotes}
        onCountChange={onNotesCountChange}
      />

      <CustomerForm
        open={formOpen}
        onOpenChange={setFormOpen}
        columns={columns}
        onCreated={addRow}
      />

      <ColumnManager
        open={colMgr}
        onOpenChange={setColMgr}
        columns={columns}
        onSave={persistColumns}
      />
      <ImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={(cols) => {
          if (cols) setColumns(cols);
          load();
        }}
      />
    </div>
  );
}
